import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";
import InstitutionTags from "components/admin/InstitutionTags";

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";

export default function AdmissionsContentPage() {
  const [steps, setSteps] = useState([]);
  const [fees, setFees] = useState([]);
  const [programmes, setProgrammes] = useState(""); // textarea, one per line
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const [loadFailed, setLoadFailed] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/admissions")) || {};
      setSteps(d.steps || []);
      setFees(d.fees || []);
      setProgrammes((d.programmes || []).join("\n"));
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updStep = (i, field, value) =>
    setSteps(steps.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  const updFee = (i, field, value) =>
    setFees(fees.map((f, idx) => (idx === i ? { ...f, [field]: value } : f)));

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const payload = {
        // Renumber steps by order (01, 02, …).
        steps: steps
          .filter((s) => (s.title || "").trim())
          .map((s, i) => ({
            step: String(i + 1).padStart(2, "0"),
            title: s.title,
            desc: s.desc || "",
            tags: s.tags || [],
          })),
        fees: fees.filter((f) => (f.class || "").trim()),
        programmes: programmes.split("\n").map((p) => p.trim()).filter(Boolean),
      };
      const saved = await adminPut("/admin/content/admissions", payload);
      if (saved) {
        setSteps(saved.steps || []);
        setFees(saved.fees || []);
        setProgrammes((saved.programmes || []).join("\n"));
      }
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}

      {error && <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>}

      {/* Steps */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Admission Process Steps"
          action={
            <button
              onClick={() => setSteps([...steps, { step: "", title: "", desc: "" }])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Step
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {steps.length === 0 && <div className="text-muted text-sm">No steps yet.</div>}
          {steps.map((s, i) => (
            <div key={i} className="border border-line rounded-xl p-3.5 flex gap-3 items-start">
              <div className="w-9 h-9 rounded-full bg-green text-white flex items-center justify-center font-serif font-bold text-sm shrink-0 mt-1">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <input value={s.title} onChange={(e) => updStep(i, "title", e.target.value)} placeholder="Step title" className={inputCls} />
                <textarea value={s.desc} onChange={(e) => updStep(i, "desc", e.target.value)} placeholder="Description" rows={2} className={`${inputCls} resize-y`} />
                <InstitutionTags value={s.tags || []} onChange={(next) => updStep(i, "tags", next)} />
              </div>
              <button onClick={() => setSteps(steps.filter((_, idx) => idx !== i))} title="Remove" className="bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44] mt-1">
                🗑
              </button>
            </div>
          ))}
        </div>
      </AdminCard>

      {/* Fees */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Fee Structure"
          action={
            <button
              onClick={() => setFees([...fees, { class: "", fee: "" }])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Row
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {fees.length === 0 && <div className="text-muted text-sm">No fee rows yet.</div>}
          {fees.map((f, i) => (
            <div key={i} className="border border-line rounded-xl p-3.5 flex flex-col gap-2">
              <div className="grid grid-cols-[1fr_1.6fr_auto] gap-3 items-center">
                <input value={f.class} onChange={(e) => updFee(i, "class", e.target.value)} placeholder="Class (e.g. Class VI–VIII)" className={inputCls} />
                <input value={f.fee} onChange={(e) => updFee(i, "fee", e.target.value)} placeholder="Fee (e.g. Admission: Tk. 3,500 | Monthly: Tk. 850)" className={inputCls} />
                <button onClick={() => setFees(fees.filter((_, idx) => idx !== i))} title="Remove" className="bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]">
                  🗑
                </button>
              </div>
              <InstitutionTags value={f.tags || []} onChange={(next) => updFee(i, "tags", next)} />
            </div>
          ))}
        </div>
      </AdminCard>

      {/* Programmes */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead title="Programmes (Apply form dropdown)" action={<span className="text-xs text-[#9aa]">One per line</span>} />
        <div className="p-[22px]">
          <textarea
            value={programmes}
            onChange={(e) => setProgrammes(e.target.value)}
            rows={8}
            placeholder={"Class VI\nClass VII\nHSC – Science\n…"}
            className={`${inputCls} resize-y font-mono text-[13px]`}
          />
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
      </div>
    </div>
  );
}
